import { useEffect, useState, type CSSProperties } from "react";
import { PageHeader, Avatar, Badge } from "@/components/admin-ui";
import { AUDIT, AUTHORS, type AuthorKey } from "@/lib/admin-mock";
import { api, ApiError } from "@/lib/api";

type Cat = "" | "editorial" | "dataengine" | "accounts" | "reconcile";

type ReconcileAuditRow = {
  id: number;
  admin_email: string;
  admin_name: string;
  action: string;
  target_table: string;
  target_key: string;
  reason: string;
  created_at: string;
};

export function AuditPage() {
  const [kindF, setKindF] = useState<Cat>("");
  const filtered = kindF && kindF !== "reconcile" ? AUDIT.filter((e) => e.kind === kindF) : AUDIT;

  // Reconcile audit (real backend, not mock). Renders as its own
  // section below the mock card — the rest of the audit feed is still
  // placeholder data until the broader /admin/audit API lands.
  const [reconcile, setReconcile] = useState<ReconcileAuditRow[]>([]);
  const [reconcileErr, setReconcileErr] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api.get<{ rows: ReconcileAuditRow[] }>(
          `/admin/dataengine/reconcile/audit?limit=50`,
        );
        if (!cancelled) {
          setReconcile(data.rows ?? []);
          setReconcileErr(null);
        }
      } catch (e: any) {
        if (!cancelled) setReconcileErr(e instanceof ApiError ? e.message : String(e));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <>
      <PageHeader
        kicker="System"
        title="Audit log"
        sub="Append-only ledger of every admin action and engine event. Searchable from ⌘K."
        actions={
          <select
            value={kindF}
            onChange={(e) => setKindF(e.target.value as Cat)}
            style={selectBox}
          >
            <option value="">All categories</option>
            <option value="editorial">Editorial</option>
            <option value="dataengine">Data engine</option>
            <option value="accounts">Accounts</option>
            <option value="reconcile">Reconciliation</option>
          </select>
        }
      />

      <div style={banner}>
        <strong>Seed data.</strong> The real append-only audit log API lands with
        the next backend pass (GET <code style={code}>/admin/audit</code>). Rows
        below are illustrative — they show the shape and tone of entries you'll
        eventually see in production.
      </div>

      {(kindF === "" || kindF === "reconcile") && (
        <div style={{ margin: "0 26px 14px", borderRadius: 10, border: "1px solid var(--rule)", background: "var(--surface)", overflow: "hidden" }}>
          <div style={{ padding: "10px 14px", borderBottom: "1px solid var(--rule)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ fontFamily: "var(--font-ui)", fontSize: 11, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--ink-4)" }}>
              Reconciliation — live
            </div>
            <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--ink-4)" }}>
              {reconcile.length} {reconcile.length === 1 ? "entry" : "entries"}
            </span>
          </div>
          {reconcileErr && (
            <div style={{ padding: "10px 14px", color: "var(--neg)", fontSize: 12 }}>{reconcileErr}</div>
          )}
          {!reconcileErr && reconcile.length === 0 && (
            <div style={{ padding: "16px 14px", color: "var(--ink-3)", fontSize: 12, fontStyle: "italic" }}>
              No reconciliation edits recorded yet. Rows appear once S3/S4/S5 write paths ship.
            </div>
          )}
          {reconcile.map((r, i) => (
            <div key={r.id} style={{
              display: "grid", gridTemplateColumns: "160px 1fr 200px 140px",
              gap: 12, padding: "10px 14px", alignItems: "start",
              borderTop: i === 0 ? undefined : "1px solid var(--rule)",
              fontSize: 12,
            }}>
              <div style={{ fontFamily: "var(--font-mono)", fontSize: 11.5, color: "var(--ink-3)" }}>
                {fmtAuditTime(r.created_at)}
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ color: "var(--ink-2)", fontWeight: 500 }}>{r.action}</div>
                <div style={{ fontSize: 11.5, color: "var(--ink-3)", marginTop: 2 }}>{r.reason}</div>
              </div>
              <div style={{ fontFamily: "var(--font-mono)", fontSize: 10.5, color: "var(--ink-4)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {r.target_table} · {r.target_key}
              </div>
              <div style={{ textAlign: "right", fontSize: 11.5, color: "var(--ink-3)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {r.admin_name || r.admin_email}
              </div>
            </div>
          ))}
        </div>
      )}

      {kindF !== "reconcile" && (
      <div style={{ margin: "0 26px", borderRadius: 10, border: "1px solid var(--rule)", background: "var(--surface)", overflow: "hidden" }}>
        {filtered.map((e, i) => {
          const isErr = e.severity === "error";
          const who =
            e.who === "you"  ? { name: "You",     initials: "HK", tint: "#1d4a3a" }
          : e.who === "hwee" ? { name: "Hwee Kim", initials: "HK", tint: "#1d4a3a" }
          : e.who === "system" ? null
          : AUTHORS[e.who as AuthorKey];
          const kindLabel =
            e.kind === "editorial"  ? "Editorial"
          : e.kind === "dataengine" ? "Data engine"
          :                           "Accounts";

          return (
            <div
              key={i}
              style={{
                display: "grid",
                gridTemplateColumns: "160px 120px 32px 1fr 120px",
                gap: 14, alignItems: "center",
                padding: "12px 16px",
                borderBottom: i === filtered.length - 1 ? "none" : "1px solid var(--rule)",
              }}
            >
              <div style={{ fontFamily: "var(--font-mono)", fontSize: 11.5, color: "var(--ink-3)" }}>{e.when}</div>
              <div>
                <Badge tone={e.kind === "editorial" ? "info" : e.kind === "dataengine" ? "ai" : "mute"}>{kindLabel}</Badge>
              </div>
              <div>
                {who ? (
                  <Avatar initials={who.initials} tint={who.tint} size={26} title={who.name} />
                ) : (
                  <span
                    style={{
                      display: "inline-flex", alignItems: "center", justifyContent: "center",
                      width: 26, height: 26, borderRadius: 999,
                      background: "var(--surface-2)", color: "var(--ink-3)",
                      fontFamily: "var(--font-mono)", fontSize: 12,
                    }}
                  >
                    ⚙
                  </span>
                )}
              </div>
              <div
                style={{
                  fontFamily: "var(--font-ui)", fontSize: 13,
                  color: isErr ? "#7a2d1f" : "var(--ink-2)", lineHeight: 1.45,
                }}
              >
                {e.text}
              </div>
              <div style={{ textAlign: "right", fontFamily: "var(--font-mono)", fontSize: 10.5, color: "var(--ink-4)" }}>
                {e.target ?? "—"}
              </div>
            </div>
          );
        })}
      </div>
      )}
      <div style={{ height: 22 }} />
    </>
  );
}

function fmtAuditTime(s: string): string {
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleString("en-SG", { dateStyle: "short", timeStyle: "short" });
}

const selectBox: CSSProperties = {
  padding: "6px 10px", borderRadius: 7,
  background: "var(--surface)", border: "1px solid var(--rule)",
  color: "var(--ink-2)", fontFamily: "var(--font-ui)", fontSize: 12,
};
const banner: CSSProperties = {
  margin: "0 26px 14px", padding: "10px 14px", borderRadius: 8,
  background: "rgba(138,90,0,0.08)", border: "1px solid rgba(138,90,0,0.20)",
  color: "#5a3e00", fontFamily: "var(--font-ui)", fontSize: 12.5,
  lineHeight: 1.55,
};
const code: CSSProperties = {
  fontFamily: "var(--font-mono)", fontSize: 11.5, color: "#5a3e00",
  background: "rgba(138,90,0,0.10)", padding: "1px 5px", borderRadius: 4,
};
